/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getI64Decoder,
  getI64Encoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU64Decoder,
  getU64Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import {
  expectAddress,
  getAccountMetaFactory,
  type ResolvedAccount,
} from "../shared";

export const STAKE_GHOST_DISCRIMINATOR = new Uint8Array([
  5, 196, 24, 54, 196, 56, 173, 191,
]);

export function getStakeGhostDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(STAKE_GHOST_DISCRIMINATOR);
}

export type StakeGhostInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountStakingAccount extends string | AccountMeta<string> = string,
  TAccountOwnerTokenAccount extends string | AccountMeta<string> = string,
  TAccountStakingVault extends string | AccountMeta<string> = string,
  TAccountStakingConfig extends string | AccountMeta<string> = string,
  TAccountGhostMint extends string | AccountMeta<string> = string,
  TAccountOwner extends string | AccountMeta<string> = string,
  TAccountTokenProgram extends string | AccountMeta<string> =
    "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA",
  TAccountSystemProgram extends string | AccountMeta<string> =
    "11111111111111111111111111111111",
  TAccountRent extends string | AccountMeta<string> =
    "SysvarRent111111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountStakingAccount extends string
        ? WritableAccount<TAccountStakingAccount>
        : TAccountStakingAccount,
      TAccountOwnerTokenAccount extends string
        ? WritableAccount<TAccountOwnerTokenAccount>
        : TAccountOwnerTokenAccount,
      TAccountStakingVault extends string
        ? WritableAccount<TAccountStakingVault>
        : TAccountStakingVault,
      TAccountStakingConfig extends string
        ? ReadonlyAccount<TAccountStakingConfig>
        : TAccountStakingConfig,
      TAccountGhostMint extends string
        ? ReadonlyAccount<TAccountGhostMint>
        : TAccountGhostMint,
      TAccountOwner extends string
        ? WritableSignerAccount<TAccountOwner> &
            AccountSignerMeta<TAccountOwner>
        : TAccountOwner,
      TAccountTokenProgram extends string
        ? ReadonlyAccount<TAccountTokenProgram>
        : TAccountTokenProgram,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      TAccountRent extends string
        ? ReadonlyAccount<TAccountRent>
        : TAccountRent,
      ...TRemainingAccounts,
    ]
  >;

export type StakeGhostInstructionData = {
  discriminator: ReadonlyUint8Array;
  amount: bigint;
  lockDuration: bigint;
};

export type StakeGhostInstructionDataArgs = {
  amount: number | bigint;
  lockDuration: number | bigint;
};

export function getStakeGhostInstructionDataEncoder(): FixedSizeEncoder<StakeGhostInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["amount", getU64Encoder()],
      ["lockDuration", getI64Encoder()],
    ]),
    (value) => ({ ...value, discriminator: STAKE_GHOST_DISCRIMINATOR }),
  );
}

export function getStakeGhostInstructionDataDecoder(): FixedSizeDecoder<StakeGhostInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["amount", getU64Decoder()],
    ["lockDuration", getI64Decoder()],
  ]);
}

export function getStakeGhostInstructionDataCodec(): FixedSizeCodec<
  StakeGhostInstructionDataArgs,
  StakeGhostInstructionData
> {
  return combineCodec(
    getStakeGhostInstructionDataEncoder(),
    getStakeGhostInstructionDataDecoder(),
  );
}

export type StakeGhostAsyncInput<
  TAccountStakingAccount extends string = string,
  TAccountOwnerTokenAccount extends string = string,
  TAccountStakingVault extends string = string,
  TAccountStakingConfig extends string = string,
  TAccountGhostMint extends string = string,
  TAccountOwner extends string = string,
  TAccountTokenProgram extends string = string,
  TAccountSystemProgram extends string = string,
  TAccountRent extends string = string,
> = {
  stakingAccount?: Address<TAccountStakingAccount>;
  ownerTokenAccount: Address<TAccountOwnerTokenAccount>;
  /**
   * Staking vault to hold all staked GHOST tokens
   * Automatically initialized on first stake using init_if_needed
   */
  stakingVault?: Address<TAccountStakingVault>;
  stakingConfig: Address<TAccountStakingConfig>;
  ghostMint: Address<TAccountGhostMint>;
  owner: TransactionSigner<TAccountOwner>;
  tokenProgram?: Address<TAccountTokenProgram>;
  systemProgram?: Address<TAccountSystemProgram>;
  rent?: Address<TAccountRent>;
  amount: StakeGhostInstructionDataArgs["amount"];
  lockDuration: StakeGhostInstructionDataArgs["lockDuration"];
};

export async function getStakeGhostInstructionAsync<
  TAccountStakingAccount extends string,
  TAccountOwnerTokenAccount extends string,
  TAccountStakingVault extends string,
  TAccountStakingConfig extends string,
  TAccountGhostMint extends string,
  TAccountOwner extends string,
  TAccountTokenProgram extends string,
  TAccountSystemProgram extends string,
  TAccountRent extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: StakeGhostAsyncInput<
    TAccountStakingAccount,
    TAccountOwnerTokenAccount,
    TAccountStakingVault,
    TAccountStakingConfig,
    TAccountGhostMint,
    TAccountOwner,
    TAccountTokenProgram,
    TAccountSystemProgram,
    TAccountRent
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  StakeGhostInstruction<
    TProgramAddress,
    TAccountStakingAccount,
    TAccountOwnerTokenAccount,
    TAccountStakingVault,
    TAccountStakingConfig,
    TAccountGhostMint,
    TAccountOwner,
    TAccountTokenProgram,
    TAccountSystemProgram,
    TAccountRent
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    stakingAccount: { value: input.stakingAccount ?? null, isWritable: true },
    ownerTokenAccount: {
      value: input.ownerTokenAccount ?? null,
      isWritable: true,
    },
    stakingVault: { value: input.stakingVault ?? null, isWritable: true },
    stakingConfig: { value: input.stakingConfig ?? null, isWritable: false },
    ghostMint: { value: input.ghostMint ?? null, isWritable: false },
    owner: { value: input.owner ?? null, isWritable: true },
    tokenProgram: { value: input.tokenProgram ?? null, isWritable: false },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
    rent: { value: input.rent ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.stakingAccount.value) {
    accounts.stakingAccount.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([115, 116, 97, 107, 105, 110, 103]),
        ),
        getAddressEncoder().encode(expectAddress(accounts.owner.value)),
      ],
    });
  }
  if (!accounts.stakingVault.value) {
    accounts.stakingVault.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            115, 116, 97, 107, 105, 110, 103, 95, 118, 97, 117, 108, 116,
          ]),
        ),
        getAddressEncoder().encode(expectAddress(accounts.stakingConfig.value)),
      ],
    });
  }
  if (!accounts.tokenProgram.value) {
    accounts.tokenProgram.value =
      "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA" as Address<"TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA">;
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }
  if (!accounts.rent.value) {
    accounts.rent.value =
      "SysvarRent111111111111111111111111111111111" as Address<"SysvarRent111111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.stakingAccount),
      getAccountMeta(accounts.ownerTokenAccount),
      getAccountMeta(accounts.stakingVault),
      getAccountMeta(accounts.stakingConfig),
      getAccountMeta(accounts.ghostMint),
      getAccountMeta(accounts.owner),
      getAccountMeta(accounts.tokenProgram),
      getAccountMeta(accounts.systemProgram),
      getAccountMeta(accounts.rent),
    ],
    data: getStakeGhostInstructionDataEncoder().encode(
      args as StakeGhostInstructionDataArgs,
    ),
    programAddress,
  } as StakeGhostInstruction<
    TProgramAddress,
    TAccountStakingAccount,
    TAccountOwnerTokenAccount,
    TAccountStakingVault,
    TAccountStakingConfig,
    TAccountGhostMint,
    TAccountOwner,
    TAccountTokenProgram,
    TAccountSystemProgram,
    TAccountRent
  >);
}

export type StakeGhostInput<
  TAccountStakingAccount extends string = string,
  TAccountOwnerTokenAccount extends string = string,
  TAccountStakingVault extends string = string,
  TAccountStakingConfig extends string = string,
  TAccountGhostMint extends string = string,
  TAccountOwner extends string = string,
  TAccountTokenProgram extends string = string,
  TAccountSystemProgram extends string = string,
  TAccountRent extends string = string,
> = {
  stakingAccount: Address<TAccountStakingAccount>;
  ownerTokenAccount: Address<TAccountOwnerTokenAccount>;
  /**
   * Staking vault to hold all staked GHOST tokens
   * Automatically initialized on first stake using init_if_needed
   */
  stakingVault: Address<TAccountStakingVault>;
  stakingConfig: Address<TAccountStakingConfig>;
  ghostMint: Address<TAccountGhostMint>;
  owner: TransactionSigner<TAccountOwner>;
  tokenProgram?: Address<TAccountTokenProgram>;
  systemProgram?: Address<TAccountSystemProgram>;
  rent?: Address<TAccountRent>;
  amount: StakeGhostInstructionDataArgs["amount"];
  lockDuration: StakeGhostInstructionDataArgs["lockDuration"];
};

export function getStakeGhostInstruction<
  TAccountStakingAccount extends string,
  TAccountOwnerTokenAccount extends string,
  TAccountStakingVault extends string,
  TAccountStakingConfig extends string,
  TAccountGhostMint extends string,
  TAccountOwner extends string,
  TAccountTokenProgram extends string,
  TAccountSystemProgram extends string,
  TAccountRent extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: StakeGhostInput<
    TAccountStakingAccount,
    TAccountOwnerTokenAccount,
    TAccountStakingVault,
    TAccountStakingConfig,
    TAccountGhostMint,
    TAccountOwner,
    TAccountTokenProgram,
    TAccountSystemProgram,
    TAccountRent
  >,
  config?: { programAddress?: TProgramAddress },
): StakeGhostInstruction<
  TProgramAddress,
  TAccountStakingAccount,
  TAccountOwnerTokenAccount,
  TAccountStakingVault,
  TAccountStakingConfig,
  TAccountGhostMint,
  TAccountOwner,
  TAccountTokenProgram,
  TAccountSystemProgram,
  TAccountRent
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    stakingAccount: { value: input.stakingAccount ?? null, isWritable: true },
    ownerTokenAccount: {
      value: input.ownerTokenAccount ?? null,
      isWritable: true,
    },
    stakingVault: { value: input.stakingVault ?? null, isWritable: true },
    stakingConfig: { value: input.stakingConfig ?? null, isWritable: false },
    ghostMint: { value: input.ghostMint ?? null, isWritable: false },
    owner: { value: input.owner ?? null, isWritable: true },
    tokenProgram: { value: input.tokenProgram ?? null, isWritable: false },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
    rent: { value: input.rent ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.tokenProgram.value) {
    accounts.tokenProgram.value =
      "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA" as Address<"TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA">;
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }
  if (!accounts.rent.value) {
    accounts.rent.value =
      "SysvarRent111111111111111111111111111111111" as Address<"SysvarRent111111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.stakingAccount),
      getAccountMeta(accounts.ownerTokenAccount),
      getAccountMeta(accounts.stakingVault),
      getAccountMeta(accounts.stakingConfig),
      getAccountMeta(accounts.ghostMint),
      getAccountMeta(accounts.owner),
      getAccountMeta(accounts.tokenProgram),
      getAccountMeta(accounts.systemProgram),
      getAccountMeta(accounts.rent),
    ],
    data: getStakeGhostInstructionDataEncoder().encode(
      args as StakeGhostInstructionDataArgs,
    ),
    programAddress,
  } as StakeGhostInstruction<
    TProgramAddress,
    TAccountStakingAccount,
    TAccountOwnerTokenAccount,
    TAccountStakingVault,
    TAccountStakingConfig,
    TAccountGhostMint,
    TAccountOwner,
    TAccountTokenProgram,
    TAccountSystemProgram,
    TAccountRent
  >);
}

export type ParsedStakeGhostInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    stakingAccount: TAccountMetas[0];
    ownerTokenAccount: TAccountMetas[1];
    /**
     * Staking vault to hold all staked GHOST tokens
     * Automatically initialized on first stake using init_if_needed
     */
    stakingVault: TAccountMetas[2];
    stakingConfig: TAccountMetas[3];
    ghostMint: TAccountMetas[4];
    owner: TAccountMetas[5];
    tokenProgram: TAccountMetas[6];
    systemProgram: TAccountMetas[7];
    rent: TAccountMetas[8];
  };
  data: StakeGhostInstructionData;
};

export function parseStakeGhostInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedStakeGhostInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 9) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      stakingAccount: getNextAccount(),
      ownerTokenAccount: getNextAccount(),
      stakingVault: getNextAccount(),
      stakingConfig: getNextAccount(),
      ghostMint: getNextAccount(),
      owner: getNextAccount(),
      tokenProgram: getNextAccount(),
      systemProgram: getNextAccount(),
      rent: getNextAccount(),
    },
    data: getStakeGhostInstructionDataDecoder().decode(instruction.data),
  };
}
